import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CategoryNavigation = ({ categories, categoryInfo, onCategoryClick }) => {
  const scrollContainerRef = useRef(null);
  const activeItemRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Function to check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      setShowLeftArrow(container.scrollLeft > 20);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 20);
    }
  };

  // Scroll active item into view when category changes
  useEffect(() => {
    if (activeItemRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeItem = activeItemRef.current;
      
      // Calculate target scroll position to center the active item
      const scrollLeft = activeItem.offsetLeft - (container.clientWidth / 2) + (activeItem.clientWidth / 2);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      
      // After scrolling, check if arrows should be visible
      setTimeout(checkScrollPosition, 400);
    }
  }, [categoryInfo.categoryIndex]);

  // Add scroll event listener to container
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Initial check
      checkScrollPosition();
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, []);

  // Handle scroll buttons
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Calculate progress percentage for the active category
  const getCurrentCategoryProgress = () => {
    const { questionInCategory, totalQuestionsInCategory } = categoryInfo;
    return ((questionInCategory - 1) / totalQuestionsInCategory) * 100;
  };

  return (
    <div className="w-full max-w-5xl mx-auto mb-8 relative">
      {/* Left scroll button - only show when needed */}
      {showLeftArrow && (
        <button 
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 text-blue-600 hover:bg-blue-50 transition-all duration-200"
          aria-label="Scroll left"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Categories scroll container */}
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto py-3 px-10 scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((category, index) => {
          const isActive = categoryInfo.categoryIndex === index;
          const isCompleted = index < categoryInfo.categoryIndex;
          const isFuture = index > categoryInfo.categoryIndex;
          
          return (
            <div 
              key={index}
              ref={isActive ? activeItemRef : null}
              onClick={() => onCategoryClick && onCategoryClick(index)}
              className={`
                flex flex-col items-center mx-3 min-w-[160px] px-4 py-4 rounded-xl cursor-pointer transition-all duration-300 relative
                ${isActive ? 'bg-blue-600 text-white shadow-lg transform scale-105' : 
                  isCompleted ? 'bg-blue-50 text-blue-700 border border-blue-200' : 
                  'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}
              `}
            >
              <div className={`mb-2 flex justify-center items-center`}>
                {isActive ? category.iconActive : category.icon}
              </div>
              
              <div className="text-sm font-medium text-center whitespace-normal leading-tight">
                {category.name}
              </div>
              
              {/* Completion status indicator (moved to top-right corner) */}
              {isCompleted && (
                <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white"></div>
              )}
              
              {/* Progress bar for active category only */}
              {isActive && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-800 bg-opacity-30 overflow-hidden rounded-b-xl">
                  <div 
                    className="h-full bg-white transition-all duration-500"
                    style={{ width: `${getCurrentCategoryProgress()}%` }}
                  ></div>
                </div>
              )}
              
              {/* Number indicator for future categories */}
              {isFuture && (
                <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                  {index + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Right scroll button - only show when needed */}
      {showRightArrow && (
        <button 
          onClick={scrollRight}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 text-blue-600 hover:bg-blue-50 transition-all duration-200"
          aria-label="Scroll right"
        >
          <ChevronRight size={24} />
        </button>
      )}
    </div>
  );
};

export default CategoryNavigation;